import Message from '../components/Message';
import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { PageSkeleton } from '../components';
import { PageHeader } from '../components/layout';
import { KeyRound, Link2, ShieldCheck, Clock3, Save, LockKeyhole } from 'lucide-react';
import './JiraSettingsPage.css';

const emptyForm = {
  jira_base_url: '',
  jira_email: '',
  jira_api_token: '',
  jira_api_token_expires_on: '',
  jira_oauth_client_id: '',
  jira_oauth_client_secret: '',
  jira_oauth_redirect_uri: '',
  webhook_secret: ''
};

const expiryClass = {
  missing: 'is-warning',
  notice: 'is-notice',
  warning: 'is-warning',
  expired: 'is-error',
  healthy: 'is-healthy'
};

const SecretStatus = ({ configured }) => (
  <span className={`jira-secret-status${configured ? ' is-configured' : ''}`}>
    {configured ? '已配置' : '未配置'}
  </span>
);

const JiraSettingsPage = ({ canWrite = false }) => {
  const [form, setForm] = useState(emptyForm);
  const [secretState, setSecretState] = useState({
    has_jira_api_token: false,
    has_jira_oauth_client_secret: false,
    has_webhook_secret: false
  });
  const [expiryStatus, setExpiryStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const response = await api.getSystemJiraConfig();
      const data = response.data || {};
      setForm({
        jira_base_url: data.jira_base_url || '',
        jira_email: data.jira_email || '',
        jira_api_token: '',
        jira_api_token_expires_on: data.jira_api_token_expires_on || '',
        jira_oauth_client_id: data.jira_oauth_client_id || '',
        jira_oauth_client_secret: '',
        jira_oauth_redirect_uri: data.jira_oauth_redirect_uri || '',
        webhook_secret: ''
      });
      setSecretState({
        has_jira_api_token: Boolean(data.has_jira_api_token),
        has_jira_oauth_client_secret: Boolean(data.has_jira_oauth_client_secret),
        has_webhook_secret: Boolean(data.has_webhook_secret)
      });
      setExpiryStatus(data.expiry_status || null);
      setMessage(null);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || '获取系统 Jira 配置失败'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        jira_base_url: form.jira_base_url.trim(),
        jira_email: form.jira_email.trim(),
        jira_api_token_expires_on: form.jira_api_token_expires_on,
        jira_oauth_client_id: form.jira_oauth_client_id.trim(),
        jira_oauth_redirect_uri: form.jira_oauth_redirect_uri.trim(),
        jira_api_token: form.jira_api_token.trim() || null,
        jira_oauth_client_secret: form.jira_oauth_client_secret.trim() || null,
        webhook_secret: form.webhook_secret.trim() || null
      };
      const response = await api.updateSystemJiraConfig(payload);
      const data = response.data || {};
      setSecretState({
        has_jira_api_token: Boolean(data.has_jira_api_token ?? secretState.has_jira_api_token),
        has_jira_oauth_client_secret: Boolean(
          data.has_jira_oauth_client_secret ?? secretState.has_jira_oauth_client_secret
        ),
        has_webhook_secret: Boolean(data.has_webhook_secret ?? secretState.has_webhook_secret)
      });
      setExpiryStatus(data.expiry_status || expiryStatus);
      setForm((current) => ({
        ...current,
        jira_api_token: '',
        jira_oauth_client_secret: '',
        webhook_secret: ''
      }));
      setMessage({ type: 'success', text: '系统 Jira 配置已保存' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || '保存系统 Jira 配置失败'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="configuration-list-page"><PageHeader title="Jira 设置" /><PageSkeleton rowCount={5} label="加载 Jira 设置中" /></div>;
  }

  return (
    <div className="jira-settings-page">
      <div className="jira-settings-heading">
        <PageHeader
          title="Jira 设置"
          actions={!canWrite && <span className="jira-readonly"><LockKeyhole size={14} aria-hidden="true" />只读权限</span>}
        />
        <p>管理系统默认凭证、OAuth 授权与 Webhook 安全配置。</p>
      </div>

      <Message message={message?.text} type={message?.type} onClose={() => setMessage(null)} />

      <form onSubmit={handleSubmit}>
        <fieldset
          disabled={!canWrite}
          className="jira-settings-fields"
        >
        <section className="configuration-settings-section">
          <div className="configuration-settings-section-header">
            <div className="jira-section-heading">
              <span className="jira-section-icon"><KeyRound size={18} aria-hidden="true" /></span>
              <div><h3>系统默认 Jira 凭证</h3><p>用于系统调用 Jira API，读取和更新工单。</p></div>
            </div>
          </div>
          <div className="configuration-settings-section-body system-config-grid">
            {expiryStatus && (
              <div className={`system-config-expiry jira-token-expiry system-config-full-row ${expiryClass[expiryStatus.level] || ''}`}>
                <Clock3 size={18} aria-hidden="true" />
                <div><strong>Token 到期状态</strong><span>{expiryStatus.message}</span></div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label" htmlFor="jira-base-url">Jira 站点地址</label>
              <input
                id="jira-base-url"
                name="jira_base_url"
                placeholder="https://your-team.atlassian.net"
                type="url"
                className="form-input"
                value={form.jira_base_url}
                onChange={updateField}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="jira-email">Jira 邮箱</label>
              <input
                id="jira-email"
                name="jira_email"
                placeholder="name@example.com"
                type="email"
                className="form-input"
                value={form.jira_email}
                onChange={updateField}
                required
              />
            </div>
            <div className="form-group">
              <div className="jira-field-heading">
                <label className="form-label" htmlFor="jira-token">Jira API Token</label>
                <SecretStatus configured={secretState.has_jira_api_token} />
              </div>
              <input
                id="jira-token"
                name="jira_api_token"
                type="password"
                autoComplete="new-password"
                className="form-input"
                value={form.jira_api_token}
                onChange={updateField}
                placeholder={secretState.has_jira_api_token ? '已配置，留空保持不变' : '请输入 Jira API Token'}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="jira-token-expiry">Token 到期日期</label>
              <input
                id="jira-token-expiry"
                name="jira_api_token_expires_on"
                aria-describedby="jira-expiry-help"
                type="date"
                className="form-input"
                value={form.jira_api_token_expires_on}
                onChange={updateField}
                required
              />
              <small id="jira-expiry-help" className="form-help">填写创建 Token 时设置的到期日期，用于到期提醒。</small>
            </div>
          </div>
        </section>

        <section className="configuration-settings-section">
          <div className="configuration-settings-section-header">
            <div className="jira-section-heading">
              <span className="jira-section-icon"><Link2 size={18} aria-hidden="true" /></span>
              <div><h3>Jira OAuth</h3><p>配置 OAuth 应用信息与授权回调地址。</p></div>
            </div>
          </div>
          <div className="configuration-settings-section-body system-config-grid">
            <div className="form-group">
              <label className="form-label" htmlFor="jira-oauth-client-id">Client ID</label>
              <input
                id="jira-oauth-client-id"
                name="jira_oauth_client_id"
                className="form-input"
                value={form.jira_oauth_client_id}
                onChange={updateField}
              />
            </div>
            <div className="form-group">
              <div className="jira-field-heading">
                <label className="form-label" htmlFor="jira-oauth-secret">Client Secret</label>
                <SecretStatus configured={secretState.has_jira_oauth_client_secret} />
              </div>
              <input
                id="jira-oauth-secret"
                name="jira_oauth_client_secret"
                type="password"
                autoComplete="new-password"
                className="form-input"
                value={form.jira_oauth_client_secret}
                onChange={updateField}
                placeholder={
                  secretState.has_jira_oauth_client_secret
                    ? '已配置，留空保持不变'
                    : '请输入 OAuth Client Secret'
                }
              />
            </div>
            <div className="form-group system-config-full-row">
              <label className="form-label" htmlFor="jira-oauth-redirect">回调地址</label>
              <input
                id="jira-oauth-redirect"
                name="jira_oauth_redirect_uri"
                type="url"
                className="form-input"
                value={form.jira_oauth_redirect_uri}
                onChange={updateField}
              />
            </div>
          </div>
        </section>

        <section className="configuration-settings-section">
          <div className="configuration-settings-section-header">
            <div className="jira-section-heading">
              <span className="jira-section-icon"><ShieldCheck size={18} aria-hidden="true" /></span>
              <div><h3>Webhook 安全</h3><p>配置 Webhook 请求校验使用的共享密钥。</p></div>
            </div>
          </div>
          <div className="configuration-settings-section-body">
            <div className="form-group">
              <div className="jira-field-heading">
                <label className="form-label" htmlFor="webhook-secret">Webhook Secret</label>
                <SecretStatus configured={secretState.has_webhook_secret} />
              </div>
              <input
                id="webhook-secret"
                name="webhook_secret"
                aria-describedby="jira-webhook-help"
                type="password"
                autoComplete="new-password"
                className="form-input"
                value={form.webhook_secret}
                onChange={updateField}
                placeholder={secretState.has_webhook_secret ? '已配置，留空保持不变' : '请输入 Webhook Secret'}
              />
              <small id="jira-webhook-help" className="form-help jira-security-note">
                修改后现有后台登录令牌会失效，需要重新登录。
              </small>
            </div>
          </div>
        </section>

        <div className="jira-settings-footer">
          <p><ShieldCheck size={16} aria-hidden="true" />{canWrite ? '密钥留空将保留现有配置，仅在需要更换时填写。' : '当前为只读模式，如需修改请联系管理员。'}</p>
          {canWrite && <button type="submit" className="btn btn-primary" disabled={saving}>
            <Save size={16} aria-hidden="true" />
            {saving ? '保存中...' : '保存系统配置'}
          </button>}
        </div>
        </fieldset>
      </form>
    </div>
  );
};

export default JiraSettingsPage;
